import { useCallback, useEffect, useRef, useState } from "react";

interface UseCssAnimationOptions {
  duration?: number;
  exitDuration?: number;
  onExitComplete?: () => void;
}

/**
 * Hook to replace AnimatePresence with CSS animations
 * Handles enter/exit animations without framer-motion
 */
export function useCssAnimation(
  isVisible: boolean,
  options: UseCssAnimationOptions = {}
) {
  const { duration = 300, exitDuration = 300, onExitComplete } = options;
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isExiting, setIsExiting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isVisible) {
      // Enter: start rendering immediately
      setShouldRender(true);
      setIsExiting(false);
    } else {
      // Exit: mark as exiting, then unmount after animation
      setIsExiting(true);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        onExitComplete?.();
      }, exitDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible, exitDuration, onExitComplete]);

  const getAnimationClasses = useCallback(
    (enterClass: string, exitClass: string) => {
      if (isExiting) return exitClass;
      if (shouldRender && isVisible) return enterClass;
      return "";
    },
    [isExiting, shouldRender, isVisible]
  );

  return {
    shouldRender,
    isExiting,
    getAnimationClasses,
  };
}

/**
 * Simple fade animation hook
 */
export function useFadeAnimation(isVisible: boolean, duration = 300) {
  return useCssAnimation(isVisible, { duration, exitDuration: duration });
}

/**
 * Simple slide animation hook
 */
export function useSlideAnimation(
  isVisible: boolean,
  direction: "up" | "down" | "left" | "right" = "up",
  duration = 300
) {
  const enterClass = `slide-in-${direction}`;
  const exitClass = `slide-out-${direction}`;
  return useCssAnimation(isVisible, { duration, exitDuration: duration });
}
